/**
 * Client binding for `generateAiPotentiel` + hook.
 *
 * Same lifecycle as `useAiEstimation` :
 *   - Subscribe to `/users/{uid}/fiches/{id}/ai/potentiel` for live pickup.
 *   - Fire the callable on mount; server decides cache-hit vs regen via
 *     `inputsHash`.
 *
 * Like `useAiEstimation`, this hook can return a "ready" state with
 * `unavailableReason` set — that's a legitimate factual answer ("CES non
 * exprimé par le PLU", "géométrie parcellaire manquante", "WFS IGN down"),
 * NOT an error. The UI renders it as a neutral message instead of the chiffre.
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

// ─── Doc shape (keep in sync with functions/src/generateAiPotentiel.ts) ──
export type AiPotentielBuilding = {
  area_m2: number;
  height_m: number | null;
  // GeoJSON Polygon | MultiPolygon sérialisé en string (règle Firestore :
  // pas d'array imbriqué — idem que parcel.geometry).
  geometry: string;
};

export type AiPotentielPlu = {
  ces_max: number | null;
  emprise_au_sol_max_pct: number | null;
  hauteur_max_m: number | null;
  hauteur_max_etages: string | null;
  recul_voie_m: number | null;
  recul_limites_m: number | null;
  espaces_verts_min_pct: number | null;
  division_parcellaire_min_m2: number | null;
  destinations_autorisees: string[];
  destinations_interdites: string[];
};

export type AiPotentiel = {
  version: 1;
  parcelGeometry: string;
  parcelArea_m2: number;
  builtFootprint_m2: number;
  builtPct: number;
  buildings: AiPotentielBuilding[];
  plu: AiPotentielPlu;
  residualFootprint_m2: number | null;
  maxFootprintAllowed_m2: number | null;
  maxLevels: string | null;
  recommendations: string[];
  caveats: string[];
  rationale: string;
  unavailableReason: string | null;
  generatedAt: string;
  model: string;
  inputsHash: string;
  pdfInclude: boolean;
};

const callGenerate = httpsCallable<{ ficheId: string }, AiPotentiel>(
  functions,
  "generateAiPotentiel",
);

// ─── Hook ────────────────────────────────────────────────────────────────
type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: AiPotentiel; fromServer: boolean }
  | { status: "error"; message: string };

export function useAiPotentiel(ficheId: string | null): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!ficheId) {
      setState({ status: "idle" });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const ref = doc(
      db,
      "users",
      user.uid,
      "fiches",
      ficheId,
      "ai",
      "potentiel",
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) return;
        const parsed = parseAiPotentiel(snap.data());
        if (parsed) {
          setState({ status: "ready", data: parsed, fromServer: false });
        }
      },
      (err) => {
        console.warn("[ai/potentiel] snapshot error", err);
      },
    );

    callGenerate({ ficheId })
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.data as AiPotentiel,
          fromServer: true,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: extractMsg(e) },
        );
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ficheId]);

  return state;
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function parseAiPotentiel(raw: DocumentData): AiPotentiel | null {
  if (!raw || raw.version !== 1) return null;

  const pluRaw = (raw.plu ?? {}) as Record<string, unknown>;
  const plu: AiPotentielPlu = {
    ces_max: num(pluRaw.ces_max),
    emprise_au_sol_max_pct: num(pluRaw.emprise_au_sol_max_pct),
    hauteur_max_m: num(pluRaw.hauteur_max_m),
    hauteur_max_etages: str(pluRaw.hauteur_max_etages),
    recul_voie_m: num(pluRaw.recul_voie_m),
    recul_limites_m: num(pluRaw.recul_limites_m),
    espaces_verts_min_pct: num(pluRaw.espaces_verts_min_pct),
    division_parcellaire_min_m2: num(pluRaw.division_parcellaire_min_m2),
    destinations_autorisees: strArr(pluRaw.destinations_autorisees),
    destinations_interdites: strArr(pluRaw.destinations_interdites),
  };

  const buildingsRaw = Array.isArray(raw.buildings) ? raw.buildings : [];
  const buildings: AiPotentielBuilding[] = [];
  for (const b of buildingsRaw) {
    if (!b || typeof b !== "object") continue;
    const rec = b as Record<string, unknown>;
    const area = num(rec.area_m2);
    const geom = str(rec.geometry);
    if (area == null || !geom) continue;
    buildings.push({
      area_m2: area,
      height_m: num(rec.height_m),
      geometry: geom,
    });
  }

  return {
    version: 1,
    parcelGeometry: typeof raw.parcelGeometry === "string" ? raw.parcelGeometry : "",
    parcelArea_m2: typeof raw.parcelArea_m2 === "number" ? raw.parcelArea_m2 : 0,
    builtFootprint_m2:
      typeof raw.builtFootprint_m2 === "number" ? raw.builtFootprint_m2 : 0,
    builtPct: typeof raw.builtPct === "number" ? raw.builtPct : 0,
    buildings,
    plu,
    residualFootprint_m2: num(raw.residualFootprint_m2),
    maxFootprintAllowed_m2: num(raw.maxFootprintAllowed_m2),
    maxLevels: str(raw.maxLevels),
    recommendations: strArr(raw.recommendations),
    caveats: strArr(raw.caveats),
    rationale: typeof raw.rationale === "string" ? raw.rationale : "",
    unavailableReason:
      typeof raw.unavailableReason === "string" &&
      raw.unavailableReason.length > 0
        ? raw.unavailableReason
        : null,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    model: typeof raw.model === "string" ? raw.model : "",
    inputsHash: typeof raw.inputsHash === "string" ? raw.inputsHash : "",
    pdfInclude: Boolean(raw.pdfInclude),
  };
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function strArr(v: unknown): string[] {
  return Array.isArray(v)
    ? v.filter((x): x is string => typeof x === "string")
    : [];
}
function extractMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(e);
}
